import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Shield, Smartphone, Mail, Loader2, ArrowLeft } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const MFAVerification = ({ tempToken, mfaMethod, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleVerify = async () => {
    if (!code || code.length < 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/mfa/verify?temp_token=${tempToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: code,
          method: mfaMethod,
          trust_device: trustDevice
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Verification successful!');
        onSuccess(data.access_token, data.device_token);
      } else {
        toast.error(data.detail || 'Invalid verification code');
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendEmailCode = async () => {
    setResending(true);
    try {
      const response = await fetch(`${API_BASE}/api/mfa/send-code?temp_token=${tempToken}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('New code sent to your email');
      } else {
        toast.error('Failed to send code');
      }
    } catch (error) {
      toast.error('Failed to send code');
    } finally {
      setResending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && code.length === 6) {
      handleVerify();
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
          {mfaMethod === 'totp' ? (
            <Smartphone className="w-8 h-8 text-amber-500" />
          ) : (
            <Mail className="w-8 h-8 text-amber-500" />
          )}
        </div>
        <CardTitle>Two-Factor Authentication</CardTitle>
        <CardDescription>
          {mfaMethod === 'totp' 
            ? 'Enter the code from your authenticator app'
            : 'Enter the code sent to your email'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="mfa-code">Verification Code</Label>
          <Input
            id="mfa-code"
            type="text"
            inputMode="numeric"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            onKeyPress={handleKeyPress}
            className="text-center text-2xl tracking-[0.5em] font-mono"
            maxLength={8}
            autoFocus
            data-testid="mfa-code-input"
          />
          <p className="text-xs text-muted-foreground text-center">
            {mfaMethod === 'totp' 
              ? 'Or enter an 8-character backup code'
              : 'Code expires in 10 minutes'}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="trust-device"
            checked={trustDevice}
            onCheckedChange={setTrustDevice}
            data-testid="trust-device-checkbox"
          />
          <Label 
            htmlFor="trust-device" 
            className="text-sm font-normal cursor-pointer"
          >
            Trust this device for 30 days
          </Label>
        </div>

        <div className="space-y-2">
          <Button
            className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold"
            onClick={handleVerify}
            disabled={loading || code.length < 6}
            data-testid="mfa-verify-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" />
                Verify
              </>
            )}
          </Button>

          {mfaMethod === 'email' && (
            <Button
              variant="ghost"
              className="w-full"
              onClick={resendEmailCode}
              disabled={resending}
            >
              {resending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Resend Code'
              )}
            </Button>
          )}

          <Button
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={onCancel}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MFAVerification;
